import { isMock } from "@/mocks";
import { Avatar, CHARACTER_TYPE } from "@vixi/types";
import { useMutation } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";
import _ from "lodash";
import { GenerateAvatarData } from "./useCreateAvatar";
import api, { assetUrl, endpoints, formdataFromObj } from "@vixi/api";
import { sleepAsync } from "@vixi/utils";

export interface GenerateAvatarRequest {
  clothes: string;
  pose?: string;
  style: string;
  image: File;
  gender?: string;
  hijabi?: string;
  purpose: "avatar" | "linkedin" | "instagram";
}

export interface CreateAvatarResponse extends Avatar {}

const mockAvatars =
  import.meta.env.MODE === "development"
    ? ((await import("@/mocks/avatar").then((mod) => mod.mockAvatars)) ?? [""])
    : [""];

const mockGenerateCourseAvatar =
  (setUploadProgress: Dispatch<SetStateAction<number>>) =>
  async (_data: GenerateAvatarData): Promise<Avatar[]> => {
    setUploadProgress(20);

    await sleepAsync(1000);

    setUploadProgress(100);

    return [
      {
        url: assetUrl(_.sample(mockAvatars) ?? ""),
        characterType: CHARACTER_TYPE.COURSE_CHARACTER,
      },
    ];
  };

const generateCourseAvatar =
  (setUploadProgress: Dispatch<SetStateAction<number>>) => async (data: GenerateAvatarData) => {
    const response = await api.post<CreateAvatarResponse>(
      endpoints.createAvatar,
      formdataFromObj({
        ...data,
        purpose: "avatar",
      } satisfies GenerateAvatarRequest),
      {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      },
    );

    response.data.url = assetUrl(response.data.url);

    return [response.data];
  };

const getMutationFn = isMock("generateCourseAvatar")
  ? mockGenerateCourseAvatar
  : generateCourseAvatar;

export const useGenerateCourseAvatar = () => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    mutationKey: ["generate-course-avatar"],
    mutationFn: getMutationFn(setUploadProgress),
  });

  return {
    ...mutation,
    uploadProgress,
  };
};
